'use client';

import Link from 'next/link'
import { motion } from 'framer-motion'
import { 
  CheckCircle2, XCircle, ArrowRight, Zap, Award
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'

interface CompetitorData {
  slug: string
  name: string
  tagline: string
  intro: string
  metrics: {
    label: string
    lenklyst: string
    competitor: string
  }[]
  features: {
    name: string
    lenklyst: boolean
    competitor: boolean
    notes: string
  }[]
  verdict: string
  whySwitchList: string[]
}

interface CompetitorComparisonClientProps {
  data: CompetitorData
}

export default function CompetitorComparisonClient({ data }: CompetitorComparisonClientProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      <main id="main-content">
        {/* Hero Header */}
        <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
          <motion.div 
            className="text-center space-y-6 max-w-3xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
              <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
                Lenklyst vs {data.name}
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
              Why Switch to<br />
              <span className="text-gray-500">Lenklyst from</span><br />
              {data.name}
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-2xl mx-auto leading-relaxed">
              {data.intro}
            </motion.p>
          </motion.div>
        </section>

        {/* Metrics Comparison Grid */}
        <section className="py-12 max-w-[1200px] mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {data.metrics.map((metric, idx) => (
              <div 
                key={idx}
                className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl flex flex-col justify-between space-y-4 hover:border-white/10 transition-colors"
              >
                <span className="text-[10px] font-mono text-white/30 uppercase tracking-wider block">
                  {metric.label}
                </span>
                <div className="space-y-1">
                  <div className="text-xs text-white/40 font-mono">Lenklyst:</div>
                  <div className="text-lg font-black text-emerald-400 font-mono">{metric.lenklyst}</div>
                </div>
                <div className="space-y-1 border-t border-white/[0.04] pt-2">
                  <div className="text-xs text-white/40 font-mono">{data.name}:</div>
                  <div className="text-base font-bold text-red-400/80 font-mono">{metric.competitor}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Feature Check Matrix Table */}
        <section className="py-16 max-w-[1000px] mx-auto px-6 relative z-10">
          <div className="text-center space-y-3 mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight">Feature Matrix Breakdown</h2>
            <p className="text-xs text-white/40 max-w-md mx-auto">Compare core capabilities and integrations side by side.</p>
          </div>

          <div className="overflow-x-auto border border-white/[0.06] rounded-2xl bg-[#0A0A0A]/60 backdrop-blur-xl">
            <table className="w-full min-w-[650px] border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-white/[0.08] bg-white/[0.02] text-[10px] font-mono uppercase tracking-wider text-white/40">
                  <th className="px-6 py-4">Capability</th>
                  <th className="px-6 py-4 text-center w-28">Lenklyst</th>
                  <th className="px-6 py-4 text-center w-28">{data.name}</th>
                  <th className="px-6 py-4">Details</th>
                </tr>
              </thead>
              <tbody>
                {data.features.map((feature, idx) => (
                  <tr key={idx} className="border-b border-white/[0.04] hover:bg-white/[0.01] transition-all">
                    <td className="px-6 py-4 font-bold text-white uppercase text-[11px] tracking-tight">
                      {feature.name}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {feature.lenklyst ? (
                          <>
                            <CheckCircle2 className="w-5 h-5 text-emerald-400 fill-emerald-500/10" aria-hidden="true" />
                            <span className="sr-only">Lenklyst: Included</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-red-500/50" aria-hidden="true" />
                            <span className="sr-only">Lenklyst: Not available</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {feature.competitor ? (
                          <>
                            <CheckCircle2 className="w-5 h-5 text-white/40" aria-hidden="true" />
                            <span className="sr-only">{data.name}: Included</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-red-500/50" aria-hidden="true" />
                            <span className="sr-only">{data.name}: Not available</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white/50 font-light leading-relaxed">
                      {feature.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Why Switch & Verdict Panels */}
        <section className="py-12 max-w-[1000px] mx-auto px-6 relative z-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          {/* Why Switch bullet points */}
          <div className="md:col-span-7 space-y-6">
            <h3 className="text-xl font-bold uppercase tracking-tight">Switching Checklist Benefits</h3>
            <ul className="space-y-3.5">
              {data.whySwitchList.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-white/70 font-light">
                  <Zap className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Final Verdict Card */}
          <div className="md:col-span-5 p-6 bg-white/[0.02] border border-white/5 rounded-2xl space-y-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 text-primary/10">
              <Award className="w-24 h-24 stroke-[1]" />
            </div>
            <div className="space-y-1">
              <span className="text-[9px] font-mono text-primary uppercase tracking-widest block">The Verdict</span>
              <h4 className="text-base font-bold text-white uppercase tracking-tight">Lenklyst Advantage</h4>
            </div>
            <p className="text-xs text-white/50 leading-relaxed font-light relative z-10">
              {data.verdict}
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
          <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Own your online presence</h2>
          <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">
            Get started with our unified marketing and branding platform for free. Connect custom domains and scale links.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <Link 
              href="/auth/sign-up" 
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95 text-sm"
            >
              Get Started Free <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              href="/pricing" 
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-white/60 border border-white/10 hover:border-white/20 hover:text-white hover:scale-105 transition-all active:scale-95 text-sm"
            >
              See Pricing Plans
            </Link>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
